<script lang="ts">
import { defineComponent } from 'vue';
import { type AclEntry, type Section, type User, useUsersStore } from '@/stores/users';
import { mapActions, mapState } from 'pinia';
import { createUser } from '@/api/admin-be';
import { toast } from '@/helpers/toast';
import Headline from '@/components/headline.vue';

export default defineComponent({
  name: 'users',
  components: { Headline },
  computed: {
    ...mapState(useUsersStore, ['records', 'sections', 'loading']),
  },
  data() {
    return {
      userName: '',
      userDescription: '',
      clipboard: '',
      editId: null as null | number,
      editValue: null as null | string,
    };
  },
  methods: {
    ...mapActions(useUsersStore, [
      'fetchRecords',
      'fetchSections',
      'updateUserAcl',
      'banRecord',
      'resetUserPassword',
      'setUserDescription',
    ]),
    getAcl(user: any, section: any) {
      const item = user.UserToSection.find((u2s: any) => u2s.sectionId === section.id);
      if (item?.write) return true;
      if (item?.read) return false;
      return null;
    },
    async toggleAclItem(user: User, section: Section) {
      let item =
        user.UserToSection.find((u2s: any) => u2s.sectionId === section.id) ||
        ({
          userId: user.id,
          sectionId: section.id,
          section,
          read: false,
          write: false,
        } as AclEntry);

      if (!item.write && !item.read) {
        item.read = true;
      } else if (item.read && !item.write) {
        item.write = true;
      } else {
        item.read = false;
        item.write = false;
      }

      await this.updateUserAcl({ ...user, UserToSection: [item] });
    },

    block(user: User) {
      this.banRecord(user);
    },

    formAddUser(event: Event) {
      if (event instanceof KeyboardEvent && event.key !== 'Enter') {
        return;
      }

      createUser(this.userName, this.userDescription)
        .then(() => {
          this.userName = '';
          this.userDescription = '';
          this.fetchRecords();
        })
        .catch((error) => {
          const { message } = error?.response?.data ?? {};

          if (message) {
            toast.add({
              severity: 'error',
              summary: 'Ошибка добавления пользователя',
              detail: message || error.message || 'Неизвестная ошибка',
              life: 5000,
              closable: true,
            });
          }
        });
    },

    copyPassword(user: User) {
      if (user.tempPassword) {
        navigator.clipboard.writeText(user.tempPassword).then(() => {
          this.clipboard = user.tempPassword ?? '';
        });
      }
    },

    openEdit(user: User) {
      this.editId = user.id;
      this.editValue = user.description;
      this.$nextTick(() => {
        if (this.$refs?.descriptionInput) {
          // @ts-expect-error $refs might be undefined, but we checked it above
          this.$refs?.descriptionInput[0].$el.focus();
        }
      });
    },

    onEditKeyUp(event: KeyboardEvent) {
      if (event.key === 'Enter') {
        const userRecord = this.records?.find((user) => user.id === this.editId);

        if (userRecord) {
          this.setUserDescription(userRecord, this.editValue || '')
            .then(() => {
              this.editId = null;
              this.$nextTick(() => {
                this.editValue = null;
              });
            })
            .catch((error) => {
              const { message } = error?.response?.data ?? {};

              if (message) {
                toast.add({
                  severity: 'error',
                  summary: 'Ошибка обновления описания пользователя',
                  detail: message || error.message || 'Неизвестная ошибка',
                  life: 5000,
                  closable: true,
                });
              }
            });
        } else {
          console.error('No user in translations, this should not happen!');
        }
      }
    },
  },
  mounted() {
    if (!this.records) {
      this.fetchRecords();
    }
    if (!this.sections) {
      this.fetchSections();
    }
  },
});
</script>

<template>
  <div class="grid gap-2">
    <template v-if="loading">
      <div class="custom-background" style="height: 90vh">Загрузка...</div>
    </template>
    <template v-else-if="!records && !sections">
      <div class="custom-background" style="height: 90vh">Нет записей или доступ ограничен</div>
    </template>
    <template v-else>
      <teleport to="#title">
        <headline class="mx-4 my-2">Пользователи</headline>
      </teleport>
      <section class="mx-3 relative">
        <table class="basic-table">
          <thead>
            <tr>
              <th>ID</th>
              <th>Имя</th>
              <th>Описание</th>
              <th colspan="2" class="w300">Временный пароль</th>
              <template v-if="sections" v-for="section in sections" :key="section.id">
                <th>{{ section.name }}</th>
              </template>
              <th>Доступ</th>
            </tr>
          </thead>
          <tbody>
            <template v-if="records" v-for="user in records" :key="user.id">
              <tr>
                <td>{{ user.id }}</td>
                <td class="w100">{{ user.name }}</td>
                <td class="w100">
                  <span v-if="editId !== user.id" @click="openEdit(user)" class="cursor-pointer">
                    {{ user.description }}
                  </span>
                  <template v-else>
                    <InputText
                      ref="descriptionInput"
                      class="w-100 text-center"
                      v-model="editValue"
                      autofocus="true"
                      @keyup="onEditKeyUp"
                      @focusout="
                        editId = null;
                        editValue = null;
                      "
                    />
                  </template>
                </td>
                <td class="w300">
                  <span
                    :class="{ 'text-blue-600 dark:text-blue-400': clipboard === user.tempPassword }"
                    >{{ user.tempPassword }}</span
                  >
                </td>
                <td class="text-nowrap">
                  <span v-if="user.tempPassword">
                    <i
                      class="pi pi-refresh mr-4 cursor-pointer"
                      title="Обновить временный пароль"
                      @click="resetUserPassword(user)"
                    />
                    <i
                      class="pi pi-copy cursor-pointer"
                      title="Скопировать в буфер"
                      @click="copyPassword(user)"
                    />
                  </span>
                  <span v-else>
                    <i
                      class="pi pi-refresh cursor-pointer"
                      title="Сбросить"
                      @click="resetUserPassword(user)"
                    />
                  </span>
                </td>
                <template v-if="sections" v-for="section in sections" :key="section.id">
                  <td class="cursor-pointer" @click="toggleAclItem(user, section)">
                    <span v-if="getAcl(user, section) === null"> - </span>
                    <span class="text-yellow-500" v-else-if="getAcl(user, section) === true">
                      запись
                    </span>
                    <span class="text-green-500" v-else> чтение </span>
                  </td>
                </template>
                <td class="cursor-pointer" @click="block(user)">
                  <i
                    :class="{
                      pi: true,
                      'pi-ban text-red-500': user.blocked,
                      'pi-check text-green-500': !user.blocked && !user.tempPassword,
                      'pi-question text-yellow-500': !user.blocked && user.tempPassword,
                    }"
                  />
                </td>
              </tr>
            </template>
            <tr>
              <td @click="formAddUser" class="cursor-pointer">
                <Button>
                  <i
                    v-if="loading"
                    class="pi pi-spinner animate-spin text-emerald-500"
                    style="font-size: 2rem"
                  />
                  <i v-else class="pi pi-plus text-emerald-500" />
                </Button>
              </td>
              <td>
                <InputText
                  v-model="userName"
                  class="rounded-md p-2 text-center"
                  placeholder="Имя пользователя"
                />
              </td>
              <td>
                <InputText
                  @keyup="formAddUser"
                  v-model="userDescription"
                  class="rounded-md p-2 mx-4 text-center"
                  placeholder="Краткое описание"
                />
              </td>
              <td colspan="1000"></td>
            </tr>
          </tbody>
        </table>
      </section>
    </template>
  </div>
</template>

<style scoped lang="scss"></style>
